import { FC, useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { ImageViewerProps } from '@types';

import ImageLoader from '@components/spinner/ImageLoader';
import Icon from '../icon/Icon';
import colors from '../../../themes/colors';

const ImageViewer: FC<ImageViewerProps> = ({
  className,
  src,
  alt,
  isExpandable,
  isMagnifierRequired = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const imageRef = useRef<HTMLImageElement>(null);

  const [isMagnifying, setIsMagnifying] = useState(false);

  const [magnifierPosition, setMagnifierPosition] = useState({
    x: 0,
    y: 0
  });

  const [zoomPreviewPosition, setZoomPreviewPosition] = useState({
    x: 0,
    y: 0
  });
  const [zoomPosition, setZoomPosition] = useState({
    x: 50,
    y: 50
  });
  const openViewer = () => {
    if (!isExpandable) {
      return;
    }

    setIsOpen(true);
  };

  const closeViewer = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    setIsLoading(true);
  }, [src]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeViewer();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
  const handleMagnifierMove = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!imageRef.current) {
      return;
    }

    const imageRect = imageRef.current.getBoundingClientRect();

    const x = event.clientX - imageRect.left;
    const y = event.clientY - imageRect.top;

    if (x < 0 || y < 0 || x > imageRect.width || y > imageRect.height) {
      return;
    }

    const xPercent = (x / imageRect.width) * 100;
    const yPercent = (y / imageRect.height) * 100;

    setMagnifierPosition({
      x,
      y
    });

    const previewSize = 320;
    const gap = 16;
    const viewportPadding = 12;

    // Prefer showing preview on the right
    let previewX = imageRect.right + gap;

    // If it doesn't fit on the right, show it on the left
    if (previewX + previewSize > window.innerWidth - viewportPadding) {
      previewX = imageRect.left - previewSize - gap;
    }

    // Always keep preview inside viewport
    previewX = Math.max(
      viewportPadding,
      Math.min(previewX, window.innerWidth - previewSize - viewportPadding)
    );

    // Center preview vertically around cursor
    let previewY = event.clientY - previewSize / 2;

    // Always keep preview inside viewport
    previewY = Math.max(
      viewportPadding,
      Math.min(previewY, window.innerHeight - previewSize - viewportPadding)
    );

    setZoomPreviewPosition({
      x: previewX,
      y: previewY
    });

    setZoomPosition({
      x: xPercent,
      y: yPercent
    });
  };
  useEffect(() => {
    if (!isMagnifying) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!imageRef.current?.contains(target)) {
        setIsMagnifying(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isMagnifying]);
  return (
    <>
      {/* Thumbnail */}
      <div
        className={`relative inline-flex max-h-[450px] min-h-[200px] max-w-[700px] overflow-hidden ${
          className ?? ''
        }`}
      >
        <button
          type="button"
          onClick={openViewer}
          disabled={!isExpandable}
          onMouseMove={isMagnifying ? handleMagnifierMove : undefined}
          className={`relative block overflow-visible rounded-md border-0 bg-transparent p-0 ${
            isMagnifying ? 'cursor-none' : 'cursor-default'
          }`}
          aria-label={isExpandable ? 'Open image' : alt}
        >
          <img
            ref={imageRef}
            src={src}
            alt={alt}
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
            className="block max-h-[450px] max-w-[700px] object-contain"
          />
          {isLoading && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <ImageLoader />
            </span>
          )}

          {/* Magnifier */}
          {isMagnifierRequired && isMagnifying && isExpandable && (
            <>
              {/* Cursor magnifier */}
              <div
                className="pointer-events-none absolute z-50 size-28 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full border-2 border-white shadow-xl"
                style={{
                  left: magnifierPosition.x,
                  top: magnifierPosition.y,
                  backgroundImage: `url(${src})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '300%',
                  backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                }}
              />

              {/* Floating zoom preview */}
              <div
                className="pointer-events-none fixed z-[99999] hidden size-80 overflow-hidden rounded-xl border border-gray-300 bg-white shadow-2xl lg:block"
                style={{
                  left: zoomPreviewPosition.x,
                  top: zoomPreviewPosition.y,
                  backgroundImage: `url(${src})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '300%',
                  backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                }}
              />
            </>
          )}
        </button>

        {/* Magnifier Button */}
        {isMagnifierRequired && isExpandable && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setIsMagnifying((previous) => !previous);
            }}
            className={`absolute right-3 top-3 z-50 flex size-10 items-center justify-center rounded-full border shadow-lg backdrop-blur-sm transition-all ${
              isMagnifying
                ? 'border-primary bg-primary'
                : 'border-gray-200 bg-white/95 hover:bg-white'
            }`}
            aria-label={isMagnifying ? 'Disable magnifier' : 'Enable magnifier'}
          >
            <Icon
              name="zoom-in"
              stroke={isMagnifying ? colors.theme : colors.primary}
            />
          </button>
        )}
      </div>
      {/* Viewer */}
      {isOpen && (
        <div
          className="bg-black/70 fixed inset-0 z-[9999] flex items-center justify-center p-3 backdrop-blur-md sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <div className="relative flex size-full max-h-[900px] max-w-7xl flex-col overflow-hidden rounded-2xl bg-primaryLite">
            {/* Close */}
            <button
              type="button"
              onClick={closeViewer}
              className="size-15  absolute right-3 top-3 z-50 flex items-center justify-center rounded-full bg-primary transition"
              aria-label="Close image viewer"
            >
              <Icon name="close" stroke={colors.theme} />
            </button>

            {/* Image area */}
            <div className="min-h-0 flex-1 p-4 sm:p-8">
              <TransformWrapper
                initialScale={1}
                minScale={0.5}
                maxScale={5}
                centerOnInit
                limitToBounds={false}
                wheel={{
                  step: 0.1
                }}
                pinch={{
                  disabled: false
                }}
                panning={{
                  disabled: false
                }}
                doubleClick={{
                  disabled: false,
                  mode: 'zoomIn'
                }}
              >
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <div className="relative size-full">
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%'
                      }}
                      contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={src}
                        alt={alt}
                        draggable={false}
                        className="max-h-full max-w-full select-none object-contain"
                      />
                    </TransformComponent>

                    {/* Zoom controls */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-3 z-40 flex justify-center">
                      <div className="pointer-events-auto flex items-center overflow-hidden rounded-full bg-primary shadow-xl">
                        <button
                          type="button"
                          onClick={() => zoomOut()}
                          className="flex size-11 items-center justify-center transition hover:opacity-80"
                          aria-label="Zoom out"
                        >
                          <Icon name="zoom-out" stroke={colors.theme} />
                        </button>

                        <button
                          type="button"
                          onClick={() => resetTransform()}
                          className="flex size-11 items-center justify-center transition hover:opacity-80"
                          aria-label="Reset zoom"
                        >
                          <Icon name="reset" stroke={colors.theme} />
                        </button>

                        <button
                          type="button"
                          onClick={() => zoomIn()}
                          className="flex size-11 items-center justify-center transition hover:opacity-80"
                          aria-label="Zoom in"
                        >
                          <Icon name="zoom-in" stroke={colors.theme} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </TransformWrapper>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageViewer;
